import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import TextBlock from '@component/common/block/text-block';
import Line from '@component/common/line';
import Gap from '@component/common/gap';
import Scroll from '@component/common/scroll';
import * as size from '@constant/size';
import * as color from '@constant/color';
import { useApplicationPurchase } from '@hook/common/usePurchase';
import Callout from '@design-system/callout';
import Chip from '@design-system/chip';
import Skeleton from '@design-system/skeleton';
import TextButton from '@design-system/text-button';
import { ETheme } from '@datatype/type';
import { useTypedDispatch, useTypedSelector } from '@hook/common/useStore';
import { useAuthPage } from '@hook/auth/useAuth';
import { actions as modalActions } from '@store/slice/modal-slice';
import { IS_IOS, IS_IYAGIGO_MOBILE_VIEW } from '@config/environment';
import {
  IYAGIGO_IOS_STORE_URL,
  IYAGIGO_ANDROID_STORE_URL,
} from '@constant/iyagigo/variable';
import { AUTH_PATH } from '@constant/auth/route-path';
import { IYAGIGO_PATH } from '@constant/iyagigo/route-path';
import ImageBlock from '@component/common/block/image-block';
import appStoreBadge from '@asset/image/app-store-badge.svg';
import googlePlayBadge from '@asset/image/google-play-badge.png';
import otterReadImage from '@asset/image/otter-read.png';

const Wrapper = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  padding: ${size.PAGE_PADDING};
`;

// Scroll(flex column) 내부에서 자식들이 짜부되지 않도록 단일 컨테이너로 감싸기 위한 컴포넌트.
// flex-shrink: 0으로 자체 콘텐츠 크기 유지 → 부모 Scroll이 overflow 처리.
const ScrollContent = styled.div`
  display: flex;
  flex-flow: column nowrap;
  width: 100%;
  flex-shrink: 0;
`;

// const ContentContainer = styled.div`
//   display: flex;
//   flex-flow: column nowrap;
//   justify-content: flex-start;
//   align-items: flex-start;
// `;

const ApplicationDownloadContainer = styled.div`
  display: flex;
  flex-flow: row nowrap;
  justify-content: flex-start;
  align-items: center;
  gap: ${size.MARGIN_1};
`;

const PurchaseStatusContainer = styled.div`
  display: flex;
  flex-flow: column nowrap;
  justify-content: flex-start;
  align-items: flex-start;
`;

const PlanCardContainer = styled.div`
  display: flex;
  flex-flow: row nowrap;
  justify-content: space-between;
  align-items: center;
  gap: ${size.MARGIN_1};
  width: 100%;
`;

const PlanCard = styled.div<{ $selected?: boolean; $disabled?: boolean }>`
  display: flex;
  flex-flow: column nowrap;
  justify-content: center;
  align-items: center;
  position: relative;
  height: 80px;
  width: 100%;
  border: 1px solid ${(props) => (props.$selected ? color.BROWN_2 : color.GRAY_2)};
  border-radius: ${size.BORDER_RADIUS_2};
  cursor: ${(props) => (props.$disabled ? 'not-allowed' : 'pointer')};
  pointer-events: ${(props) => (props.$disabled ? 'none' : 'auto')};
  opacity: ${(props) => (props.$disabled ? 0.6 : 1)};
  background: ${(props) => (props.$selected ? color.BROWN_4 : color.WHITE_1)};
`;

const DiscountBadge = styled.div`
  position: absolute;
  top: -14px;
  left: -4px;
`;

const RestoreContainer = styled.div`
  display: flex;
  flex-flow: row nowrap;
  justify-content: space-between;
  align-items: center;
  width: 100%;
`;

const TermsContainer = styled.div`
  display: flex;
  flex-flow: row nowrap;
  justify-content: flex-start;
  align-items: center;
  gap: ${size.MARGIN_1};
`;

const OtterMessageContainer = styled.div`
  display: flex;
  flex-flow: row nowrap;
  align-items: center;
  gap: ${size.MARGIN_1};
  width: 100%;
`;

const ComparisonTable = styled.div`
  display: flex;
  flex-flow: column nowrap;
  width: 100%;
  border: 1px solid ${color.BROWN_3};
  border-radius: ${size.BORDER_RADIUS_1};
  overflow: hidden;
  background-color: ${color.BROWN_5};
`;

const ComparisonHeaderRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 80px 80px;
  align-items: stretch;
  background-color: ${color.BROWN_4};
`;

const ComparisonRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 80px 80px;
  align-items: stretch;
  border-top: 1px solid ${color.BROWN_3};
`;

const ComparisonLabelCell = styled.div`
  display: flex;
  align-items: center;
  padding: 10px ${size.MARGIN_1};
`;

const ComparisonCell = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 10px ${size.MARGIN_1};
`;

const ComparisonPremiumCell = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 10px ${size.MARGIN_1};
`;

const ComparisonLabelContainer = styled.div`
  display: flex;
  flex-flow: column nowrap;
  align-items: flex-start;
  gap: 4px;
`;

const COMPARISON_LABEL_DESIGN = {
  text: { size: 'small', color: color.BLACK_2, bold: true },
  box: { alignX: 'left' },
} as const;

const COMPARISON_SUB_DESIGN = {
  text: { size: 'tiny', color: color.GRAY_1, bold: false },
  box: { alignX: 'left' },
} as const;

const COMPARISON_HEADER_FREE_DESIGN = {
  text: { size: 'small', color: color.GRAY_1, bold: true },
} as const;

const COMPARISON_HEADER_PREMIUM_DESIGN = {
  text: { size: 'small', color: color.BROWN_1, bold: true },
} as const;

const COMPARISON_CHECK_DESIGN = {
  text: { size: 'medium', color: color.BROWN_1, bold: true },
} as const;

const COMPARISON_DASH_DESIGN = {
  text: { size: 'medium', color: color.GRAY_1, bold: false },
} as const;

const KEY_DESIGN = { text: { size: 'medium', bold: false } } as const;
const VALUE_DESIGN = { text: { size: 'medium', height: '25px', color: color.GRAY_1 } } as const;

const formatDate = (dateString: string | null): string => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  return `${date.getFullYear()}.${String(date.getMonth() + 1).padStart(2, '0')}.${String(date.getDate()).padStart(2, '0')}`;
};

const getPlanLabel = (plan: string | null): string => {
  if (!plan) return '-';
  if (plan.toLowerCase().includes('monthly')) return '월간 구독';
  if (plan.toLowerCase().includes('yearly')) return '연간 구독';
  return plan;
};

// 구독 화면 UI 점검용. 점검 후 false로 돌리면 브라우저의 앱 안내 화면을 복원한다.
const BROWSER_SUBSCRIPTION_PREVIEW = false;

const Subscription = () => {
  const navigate = useNavigate();
  const dispatch = useTypedDispatch();
  const isAuthenticated = useTypedSelector((state) => state.rootReducer?.userReducer?.isAuthenticated) ?? false;
  const { openAuthPage } = useAuthPage();
  const { offerings, status, isLoading, isApp, executePurchase, restorePurchase, fetchPurchaseOffering } =
    useApplicationPurchase();
  const [selectedProductId, setSelectedProductId] = React.useState<string | null>(null);

  useEffect(() => {
    fetchPurchaseOffering();
  }, [fetchPurchaseOffering]);

  const notify = (message: string) => {
    dispatch(modalActions.showDialogModal({ type: 'plain-text', data: message }));
  };

  const handlePurchase = async () => {
    // 중복 클릭 방지 (구매 진행 중 재클릭 시 결제 중복 호출 방지)
    if (!isApp || !selectedProductId || isLoading) return;
    // 비회원: 가입 먼저 (가입 후 구독 페이지로 복귀해 결제 진행)
    if (!isAuthenticated) {
      openAuthPage('signin');
      return;
    }
    const result = await executePurchase(selectedProductId);
    if (result?.userCancelled) return;
    if (result?.status === 'success') {
      dispatch(modalActions.showDialogModal({ type: 'subscription-start', data: null }));
    } else if (result?.error) {
      notify(`구독에 실패했습니다: ${result.error.message}`);
    }
  };

  const handleRestore = async () => {
    // 중복 클릭 방지 (텍스트 링크라 UI 로딩 처리 없음 → 핸들러 가드가 유일한 안전장치)
    if (!isApp || isLoading) return;
    const result = await restorePurchase();
    if (result?.status === 'success') {
      notify('구매가 복원되었습니다.');
    } else {
      notify('복원할 구매가 없습니다.');
    }
  };

  // 앱이 아닌 경우
  if (!isApp && !BROWSER_SUBSCRIPTION_PREVIEW) {
    return (
      <Wrapper>
        <TextBlock
          text='앱에서 구독할 수 있습니다.'
          design={{ text: { size: 'medium', color: color.GRAY_1 }, box: { alignX: 'left' } }}
        />
        <Gap height={size.MARGIN_1} />
        {IS_IYAGIGO_MOBILE_VIEW ? (
          <TextButton
            text='앱에서 보기'
            theme={ETheme.BROWN}
            kind='filled'
            size='medium'
            width='100%'
            shape='pill'
            handleClick={() => window.open(IS_IOS ? IYAGIGO_IOS_STORE_URL : IYAGIGO_ANDROID_STORE_URL, '_blank')}
          />
        ) : (
          <ApplicationDownloadContainer>
            <ImageBlock
              src={appStoreBadge}
              alt='App Store'
              design={{ image: { height: '40px' }, box: { event: { cursor: 'pointer' } } }}
              click={{ handler: () => window.open(IYAGIGO_IOS_STORE_URL, '_blank') }}
            />
            <ImageBlock
              src={googlePlayBadge}
              alt='Google Play'
              design={{ image: { height: '40px' }, box: { event: { cursor: 'pointer' } } }}
              click={{ handler: () => window.open(IYAGIGO_ANDROID_STORE_URL, '_blank') }}
            />
          </ApplicationDownloadContainer>
        )}
        <Gap height={size.MARGIN_1} />
        <Line color={color.GRAY_3} />
      </Wrapper>
    );
  }

  // 미구독 - 플랜 선택
  const monthlyOffering = offerings.find((offering) => offering.period === 'monthly');
  const annualOffering = offerings.find((offering) => offering.period === 'annual');

  const discountPercent =
    monthlyOffering && annualOffering ? Math.round((1 - annualOffering.price / 12 / monthlyOffering.price) * 100) : 0;

  return (
    <Wrapper>
      <Scroll direction='y' height={`calc(100%)`}>
        <ScrollContent>
          {status?.isActive ? (
            <PurchaseStatusContainer>
              <TextBlock text='현재 플랜' design={KEY_DESIGN} />
              <TextBlock text={getPlanLabel(status.plan)} design={VALUE_DESIGN} />
              <Gap height={size.MARGIN_1} />
              <TextBlock text='만료일' design={KEY_DESIGN} />
              <TextBlock text={formatDate(status.expirationDate)} design={VALUE_DESIGN} />
              <Gap height={size.MARGIN_1} />
              <TextBlock text='자동 갱신' design={KEY_DESIGN} />
              <TextBlock text={status.willRenew ? '사용' : '사용 안 함'} design={VALUE_DESIGN} />
              <Gap height={size.MARGIN_1} />
              <TextBlock
                text='구독해지는 App Store 또는 Google Play > 구독에서 가능합니다.'
                design={{ text: { size: 'small', color: color.GRAY_1 }, box: { alignX: 'left' } }}
              />
            </PurchaseStatusContainer>
          ) : (
            <>
              <OtterMessageContainer>
                <ImageBlock src={otterReadImage} alt='이야기고 Plus⁺' design={{ image: { height: '80px' } }} />
                <Callout
                  text={'커피 한 잔 값으로 ☕\n매일 학습 습관 들이기'}
                  direction='left'
                  theme={ETheme.CREAM}
                  size='medium'
                  width='fit-content'
                />
              </OtterMessageContainer>
              <Gap height={size.MARGIN_2} />
              <ComparisonTable>
                <ComparisonHeaderRow>
                  <ComparisonLabelCell />
                  <ComparisonCell>
                    <TextBlock text='Free' design={COMPARISON_HEADER_FREE_DESIGN} />
                  </ComparisonCell>
                  <ComparisonPremiumCell>
                    <TextBlock text='Plus⁺' design={COMPARISON_HEADER_PREMIUM_DESIGN} />
                  </ComparisonPremiumCell>
                </ComparisonHeaderRow>
                <ComparisonRow>
                  <ComparisonLabelCell>
                    <ComparisonLabelContainer>
                      <TextBlock text='말하기 연습 +' design={COMPARISON_LABEL_DESIGN} />
                      <TextBlock text='· 더 많은 말하기 연습' design={COMPARISON_SUB_DESIGN} />
                    </ComparisonLabelContainer>
                  </ComparisonLabelCell>
                  <ComparisonCell>
                    <TextBlock text='✗' design={COMPARISON_DASH_DESIGN} />
                  </ComparisonCell>
                  <ComparisonPremiumCell>
                    <TextBlock text='✓' design={COMPARISON_CHECK_DESIGN} />
                  </ComparisonPremiumCell>
                </ComparisonRow>
                <ComparisonRow>
                  <ComparisonLabelCell>
                    <ComparisonLabelContainer>
                      <TextBlock text='빈칸문제 학습 +' design={COMPARISON_LABEL_DESIGN} />
                      <TextBlock text='· 더 많은 문제' design={COMPARISON_SUB_DESIGN} />
                      <TextBlock text='· 난이도·문제 개수 조절' design={COMPARISON_SUB_DESIGN} />
                      <TextBlock text='· 나만의 문제집' design={COMPARISON_SUB_DESIGN} />
                    </ComparisonLabelContainer>
                  </ComparisonLabelCell>
                  <ComparisonCell>
                    <TextBlock text='✗' design={COMPARISON_DASH_DESIGN} />
                  </ComparisonCell>
                  <ComparisonPremiumCell>
                    <TextBlock text='✓' design={COMPARISON_CHECK_DESIGN} />
                  </ComparisonPremiumCell>
                </ComparisonRow>
                {/* 종료된 서비스 — 코드 보존
                <ComparisonRow>
                  <ComparisonLabelCell>
                    <ComparisonLabelContainer>
                      <TextBlock text='표현·이야기 학습 +' design={COMPARISON_LABEL_DESIGN} />
                      <TextBlock text='· 모든 표현·이야기' design={COMPARISON_SUB_DESIGN} />
                    </ComparisonLabelContainer>
                  </ComparisonLabelCell>
                  <ComparisonCell>
                    <TextBlock text='✗' design={COMPARISON_DASH_DESIGN} />
                  </ComparisonCell>
                  <ComparisonPremiumCell>
                    <TextBlock text='✓' design={COMPARISON_CHECK_DESIGN} />
                  </ComparisonPremiumCell>
                </ComparisonRow>
                */}
                <ComparisonRow>
                  <ComparisonLabelCell>
                    <ComparisonLabelContainer>
                      <TextBlock text='단어집 학습 +' design={COMPARISON_LABEL_DESIGN} />
                      <TextBlock text='· 모든 단어집' design={COMPARISON_SUB_DESIGN} />
                      <TextBlock text='· 나만의 단어집' design={COMPARISON_SUB_DESIGN} />
                    </ComparisonLabelContainer>
                  </ComparisonLabelCell>
                  <ComparisonCell>
                    <TextBlock text='✗' design={COMPARISON_DASH_DESIGN} />
                  </ComparisonCell>
                  <ComparisonPremiumCell>
                    <TextBlock text='✓' design={COMPARISON_CHECK_DESIGN} />
                  </ComparisonPremiumCell>
                </ComparisonRow>
              </ComparisonTable>
              <Gap height={size.MARGIN_5} />
              {monthlyOffering && annualOffering ? (
                <PlanCardContainer>
                  <PlanCard
                    key={monthlyOffering.productId}
                    $selected={selectedProductId === monthlyOffering.productId}
                    $disabled={isLoading}
                    onClick={() => {
                      if (isLoading) return;
                      setSelectedProductId(monthlyOffering.productId);
                    }}>
                    <TextBlock text={'월간 구독'} design={{ text: { size: 'medium', bold: true } }} />
                    <TextBlock
                      text={`월 ${monthlyOffering.priceString.charAt(0)}${Math.round(monthlyOffering.price).toLocaleString()}`}
                      design={{ text: { size: 'medium', bold: true, color: color.BROWN_1 } }}
                    />
                  </PlanCard>
                  <PlanCard
                    key={annualOffering.productId}
                    $selected={selectedProductId === annualOffering.productId}
                    $disabled={isLoading}
                    onClick={() => {
                      if (isLoading) return;
                      setSelectedProductId(annualOffering.productId);
                    }}>
                    {discountPercent > 0 && (
                      <DiscountBadge>
                        <Chip
                          text={`${discountPercent}% 할인`}
                          size='tiny'
                          kind='filled'
                          theme={ETheme.YELLOW}
                          usage='static'
                        />
                      </DiscountBadge>
                    )}
                    <TextBlock text={'연간 구독'} design={{ text: { size: 'medium', bold: true } }} />
                    <TextBlock
                      text={`연 ${annualOffering.priceString.charAt(0)}${Math.round(annualOffering.price).toLocaleString()}`}
                      design={{ text: { size: 'medium', bold: true, color: color.BROWN_1 } }}
                    />
                    <Chip
                      text={`월 ${annualOffering.priceString.charAt(0)}${Math.round(annualOffering.price / 12).toLocaleString()}`}
                      size='tiny'
                      width={'70px'}
                      kind='filled'
                      theme={ETheme.BROWN}
                      usage='static'
                    />
                  </PlanCard>
                </PlanCardContainer>
              ) : (
                <PlanCardContainer>
                  <Skeleton height='80px' borderRadius={size.BORDER_RADIUS_2} />
                  <Skeleton height='80px' borderRadius={size.BORDER_RADIUS_2} />
                </PlanCardContainer>
              )}
              <Gap height={size.MARGIN_1} />
              <TextButton
                text={!isAuthenticated ? '로그인하고 구독하기' : '구독하기'}
                theme={ETheme.BROWN}
                kind='filled'
                size='medium'
                width='100%'
                shape='pill'
                handleClick={handlePurchase}
                isLoading={isLoading}
                disabled={!selectedProductId}
              />
              <Gap height={size.MARGIN_1} />
              <RestoreContainer>
                <TermsContainer>
                  <TextBlock
                    text='이용약관'
                    design={{
                      text: { size: 'small', color: color.GRAY_1, underline: true },
                      box: { event: { cursor: 'pointer' } },
                    }}
                    click={{
                      handler: () =>
                        navigate(AUTH_PATH.SERVICE_TERM_PATH, {
                          replace: true,
                          state: { closePath: IYAGIGO_PATH.HUB_SUBSCRIPTION_PATH },
                        }),
                    }}
                  />
                  <TextBlock
                    text='개인정보처리방침'
                    design={{
                      text: { size: 'small', color: color.GRAY_1, underline: true },
                      box: { event: { cursor: 'pointer' } },
                    }}
                    click={{
                      handler: () =>
                        navigate(AUTH_PATH.PRIVATE_TERM_PATH, {
                          replace: true,
                          state: { closePath: IYAGIGO_PATH.HUB_SUBSCRIPTION_PATH },
                        }),
                    }}
                  />
                </TermsContainer>
                <TextBlock
                  text='구매 복원'
                  design={{
                    text: { size: 'small', color: color.GRAY_1 },
                    box: { event: { cursor: 'pointer' }, width: 'content-fit' },
                  }}
                  click={{ handler: handleRestore }}
                />
              </RestoreContainer>
            </>
          )}
        </ScrollContent>
      </Scroll>
    </Wrapper>
  );
};

export default Subscription;
